import React from 'react';
import { motion } from 'framer-motion';
import {
  Home,
  Zap,
  Star,
  Users,
  User,
} from 'lucide-react';
import { sound } from '../../utils/sound.js';

export type DockTab = 'home' | 'main' | 'auto' | 'stars' | 'referrals' | 'profile' | 'activity';

export interface FloatingGlassDockProps {
  activeTab: DockTab;
  onSelectTab: (tab: DockTab) => void;
  triggerHaptic?: () => void;
}

export const FloatingGlassDock: React.FC<FloatingGlassDockProps> = ({
  activeTab,
  onSelectTab,
  triggerHaptic,
}) => {
  const handleTabClick = (tab: DockTab) => {
    sound.playTap();
    if (triggerHaptic) triggerHaptic();
    onSelectTab(tab);
  };

  const navItems = [
    {
      id: 'home' as const,
      label: 'Home',
      icon: Home,
      accentColor: 'text-cyan-400',
      activeGradient: 'from-cyan-500/25 via-indigo-500/20 to-purple-500/25',
      activeBorder: 'border-cyan-500/40',
    },
    {
      id: 'main' as const, // Maps to Mining screen
      label: 'Mining',
      icon: Zap,
      accentColor: 'text-amber-400',
      activeGradient: 'from-amber-500/25 via-yellow-500/20 to-amber-600/25',
      activeBorder: 'border-amber-500/40',
    },
    {
      id: 'stars' as const,
      label: 'Stars',
      icon: Star,
      accentColor: 'text-yellow-300',
      activeGradient: 'from-yellow-400/30 via-amber-500/25 to-purple-500/25',
      activeBorder: 'border-yellow-400/50',
    },
    {
      id: 'referrals' as const,
      label: 'Referrals',
      icon: Users,
      accentColor: 'text-pink-400',
      activeGradient: 'from-pink-500/25 via-fuchsia-500/20 to-purple-500/25',
      activeBorder: 'border-pink-500/40',
    },
    {
      id: 'profile' as const,
      label: 'Profile',
      icon: User,
      accentColor: 'text-purple-300',
      activeGradient: 'from-purple-500/25 via-indigo-500/20 to-cyan-500/25',
      activeBorder: 'border-purple-500/40',
    },
  ];

  // Map 'auto' or 'activity' to relevant dock items if active
  const isMiningActive = activeTab === 'main' || activeTab === 'auto';

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-40 max-w-lg mx-auto px-4 pb-[calc(env(safe-area-inset-bottom,16px)+8px)] pt-2 pointer-events-none"
      role="navigation"
      aria-label="Main Navigation Dock"
    >
      <div className="floating-dock-glass rounded-[2rem] p-1.5 flex items-center justify-between border shadow-2xl pointer-events-auto backdrop-blur-2xl">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            item.id === 'main'
              ? isMiningActive
              : activeTab === item.id;

          return (
            <button
              key={item.id}
              onClick={() => handleTabClick(item.id)}
              className="relative flex-1 py-2 sm:py-2.5 px-1 rounded-2xl flex flex-col items-center justify-center space-y-1 transition-all duration-200 outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              style={{ minHeight: 52 }}
            >
              {/* Animated Active Pill Indicator */}
              {isActive && (
                <motion.div
                  layoutId="dockActivePill"
                  transition={{ type: 'spring', stiffness: 420, damping: 32 }}
                  className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${item.activeGradient} border ${item.activeBorder} shadow-lg`}
                />
              )}

              {/* Icon Container with Micro-bounce on Tap */}
              <motion.div
                whileTap={{ scale: 0.85 }}
                className="relative z-10 flex items-center justify-center"
              >
                <Icon
                  className={`w-5 h-5 transition-transform duration-200 ${
                    isActive
                      ? `${item.accentColor} scale-110 drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]`
                      : 'text-gray-400 group-hover:text-gray-200'
                  } ${item.id === 'stars' && isActive ? 'fill-current' : ''}`}
                />
              </motion.div>

              {/* Label */}
              <span
                className={`relative z-10 text-[10px] sm:text-[11px] font-bold tracking-tight transition-colors duration-200 ${
                  isActive ? 'text-white font-extrabold' : 'text-gray-400'
                }`}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
